/**
 * /operations/payroll/bank-file — Session 3B PR 16.
 * Owning agent: Rawatib. Per-bank salary files.
 */

import { useQuery } from "@tanstack/react-query";
import { Building2, CheckCircle2, Download, FileText } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ErrorPanel, Loading } from "../components/Loading";
import { InlineAISuggestion, PageHeader, TraceableValue } from "../components/ui";
import { useLegalEntityGate } from "../lib/legalEntity";
import { session3bApi } from "../lib/session3b-api";
import type { BankFileRunItem } from "../lib/session3b-api";
import { AiBriefPanel } from "../shell/AiBriefPanel";
import { showAlertTrace } from "./_session3aHelpers";

const _BANK_LABELS: Record<BankFileRunItem["bank"], string> = {
  riyad: "Riyad Bank",
  alrajhi: "Al Rajhi",
  snb: "SNB",
  anb: "ANB",
  albilad: "Al Bilad",
  other: "Other",
};

type BankFilter = "all" | BankFileRunItem["bank"];

function _statusToneClass(s: BankFileRunItem["submission_status"]): string {
  if (s === "confirmed") return "bg-emerald-100 text-emerald-800";
  if (s === "submitted") return "bg-brand-blue/15 text-brand-deep";
  return "bg-amber-100 text-amber-800";
}

function _fmtSar(n: number): string {
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function BankFileOutputPage() {
  const { t } = useTranslation();
  const { legalEntityId, gate } = useLegalEntityGate();
  const [bankFilter, setBankFilter] = useState<BankFilter>("all");

  const list = useQuery({
    queryKey: ["session3b-bank-files", legalEntityId],
    queryFn: () => session3bApi.listBankFileRuns(legalEntityId ?? ""),
    enabled: !!legalEntityId,
  });

  const items: BankFileRunItem[] = list.data?.items ?? [];
  const filtered = useMemo(() => {
    if (bankFilter === "all") return items;
    return items.filter((r) => r.bank === bankFilter);
  }, [items, bankFilter]);

  const counts = useMemo(() => {
    const pending = items.filter((r) => r.submission_status === "draft").length;
    const banks = new Set(items.map((r) => r.bank));
    const total_amount = items.reduce((a, r) => a + r.total_amount_sar, 0);
    return {
      total: items.length,
      attention: pending,
      critical: 0,
      banksCount: banks.size,
      totalAmount: total_amount,
    };
  }, [items]);

  if (gate) return gate;

  return (
    <div className="grid grid-cols-[1fr_280px] gap-4">
      <div className="space-y-3">
        <PageHeader
          title={t("payroll.bank.title", "Bank File Output")}
          subtitle={t(
            "payroll.bank.subtitle",
            "Per-bank salary files: Riyad · Al Rajhi · SNB · ANB · Al Bilad. Generate per period; HR uploads to bank portals.",
          )}
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1.5">
            {(["all", "riyad", "alrajhi", "snb", "anb", "albilad", "other"] as BankFilter[]).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setBankFilter(f)}
                className={`rounded-full border px-3 py-1 text-xs font-medium ${
                  bankFilter === f
                    ? "border-brand-blue bg-brand-blue/10 text-brand-deep"
                    : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
                }`}
              >
                {f === "all" ? t("payroll.bank.filter.all", "All") : _BANK_LABELS[f as BankFileRunItem["bank"]]}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() =>
              showAlertTrace(
                t(
                  "payroll.bank.cta.generate_all_note",
                  "Generates one bank file per bank in use (split by EmployeeORM.bank_iban prefix).",
                ),
              )
            }
            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
          >
            <Download className="h-4 w-4" />
            {t("payroll.bank.cta.generate_all", "Generate All Bank Files")}
          </button>
        </div>

        {counts.attention > 0 && (
          <InlineAISuggestion
            agent="rawatib"
            icon={<FileText className="h-4 w-4" />}
            severity="warning"
            headline={t(
              "payroll.bank.suggestion.pending_headline",
              `${counts.attention} bank file${counts.attention === 1 ? "" : "s"} ready · awaiting submission.`,
            )}
            body={t(
              "payroll.bank.suggestion.pending_body",
              "Rawatib notes: download each draft and upload via the respective bank portal. Mark submitted once the bank confirms.",
            )}
            primaryAction={{
              label: t("payroll.bank.suggestion.show_drafts", "Show drafts"),
              onClick: () => setBankFilter("all"),
            }}
            suggestionKey="bank-pending"
          />
        )}
        {counts.total === 0 && !list.isLoading && (
          <InlineAISuggestion
            agent="rawatib"
            icon={<CheckCircle2 className="h-4 w-4" />}
            severity="info"
            headline={t(
              "payroll.bank.suggestion.empty_headline",
              "No bank files generated yet.",
            )}
            body={t(
              "payroll.bank.suggestion.empty_body",
              "Rawatib notes: hit Generate All to render bank files for posted periods.",
            )}
            primaryAction={{
              label: t("payroll.bank.suggestion.open_period_lock", "Open Period Lock"),
              onClick: () => window.location.assign("/operations/payroll/period-lock"),
            }}
            suggestionKey="bank-empty"
          />
        )}

        {list.isLoading && <Loading />}
        {list.isError && <ErrorPanel message={(list.error as Error).message} />}

        {filtered.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-border-subtle bg-white">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  <th className="px-3 py-2">{t("payroll.bank.col.period", "Period")}</th>
                  <th className="px-3 py-2">{t("payroll.bank.col.bank", "Bank")}</th>
                  <th className="px-3 py-2">{t("payroll.bank.col.generated", "Generated")}</th>
                  <th className="px-3 py-2">{t("payroll.bank.col.employees", "Employees")}</th>
                  <th className="px-3 py-2">{t("payroll.bank.col.amount", "Amount (SAR)")}</th>
                  <th className="px-3 py-2">{t("payroll.bank.col.hash", "Hash")}</th>
                  <th className="px-3 py-2">{t("payroll.bank.col.status", "Status")}</th>
                  <th className="px-3 py-2">{t("payroll.bank.col.actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr key={row.run_id} className="group border-t border-border-subtle hover:bg-surface-sunken/40">
                    <td className="px-3 py-2 text-text-secondary">{row.period_label}</td>
                    <td className="px-3 py-2 text-text-secondary">
                      <span className="inline-flex items-center gap-1">
                        <Building2 className="h-3 w-3" />
                        {_BANK_LABELS[row.bank]}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-text-secondary">
                      <span dir="ltr">{row.generated_at}</span>
                    </td>
                    <td className="px-3 py-2 text-text-secondary">{row.employees_count}</td>
                    <td className="px-3 py-2">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "payroll.bank.trace.amount",
                              `total_amount_sar = sum over Net per employee whose bank_iban starts with this bank's BIN. Today every employee maps to 'other' until per-bank IBAN routing lands.`,
                            ),
                          )
                        }
                      >
                        <span className="font-mono text-xs">{_fmtSar(row.total_amount_sar)}</span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2 font-mono text-[10px] text-text-tertiary">{row.file_hash ?? "—"}</td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${_statusToneClass(row.submission_status)}`}
                      >
                        {row.submission_status}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() =>
                          showAlertTrace(
                            t(
                              "payroll.bank.action.download_note",
                              `Download bank file for ${_BANK_LABELS[row.bank]}.`,
                            ),
                          )
                        }
                        className="inline-flex items-center gap-1 rounded border border-brand-blue px-1.5 py-0.5 text-[10px] font-medium text-[#2260C9] opacity-50 transition-opacity group-hover:opacity-100 hover:bg-brand-blue/10"
                      >
                        <Download className="h-3 w-3" />
                        {t("payroll.bank.action.download", "Download")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <aside className="rounded-2xl border border-border-subtle bg-white">
        <AiBriefPanel
          agent="rawatib"
          liveContext={{
            total: counts.total,
            attention: counts.attention,
            critical: 0,
            top_kind: t("payroll.bank.title", "Bank File Output"),
            window_label: `${counts.banksCount} bank${counts.banksCount === 1 ? "" : "s"} · SAR ${_fmtSar(counts.totalAmount)}`,
          }}
        />
      </aside>
    </div>
  );
}
